<script>
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {ref} from "vue";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import {useOverviewStore} from "@/javascript/stores/overview";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import IntervalToggle from "@/javascript/views/components/overview/IntervalToggle.vue";
import DataCard from "@/javascript/views/components/overview/DataCard.vue";
import BaseDoughnutChart from "@/javascript/views/components/charts/BaseDoughnutChart.vue";
import BaseLineChart from "@/javascript/views/components/charts/BaseLineChart.vue";
import BaseDataTable from "@/javascript/views/components/charts/BaseDataTable.vue";
import DonationPaymentChart from "../../components/DonationPaymentChart.vue";
import CampaignCard from "../../components/landing/CampaignCard.vue";
import DoughnutChartDataCard from "../../components/charts/DoughnutChartDataCard.vue";
import KpiCard from "../../components/overview/KpiCard.vue";
import RecentDonationsCard from "../../components/overview/RecentDonationsCard.vue";
import CampaignGalleryCard from "../../components/overview/CampaignGalleryCard.vue";
import CampaignCalendar from "../../components/overview/CampaignCalendar.vue";
import FunnelBar from "../../components/overview/FunnelBar.vue";
import {useOrganizationStore} from "../../../stores/organizations";
import Tooltip from "@/javascript/views/components/tooltip.vue";
import { CHART_COLORS } from '@/javascript/helpers/chartColors';
import DashboardSettingsModal from "../../components/overview/DashboardSettingsModal.vue";
import CommunityHeatMap from "../../components/overview/CommunityHeatMap.vue";

export default {
  name: "OverviewPanel",
  components: {
    Tooltip,
    DoughnutChartDataCard,
    CampaignCard,
    DonationPaymentChart, BaseLineChart, IntervalToggle, DataCard, BaseDoughnutChart, BaseDataTable, KpiCard, RecentDonationsCard, CampaignGalleryCard, CampaignCalendar, FunnelBar, DashboardSettingsModal, CommunityHeatMap
  },
  setup() {
    // Router
    const router = useRouter();
    const route = useRoute();
    const showSettingsMenu = ref(false);
    const showSettingsModal = ref(false);

    // i18n
    const {t} = useI18n();
    const overviewStore = useOverviewStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    if (overviewStore.organizationId) {
      overviewStore.loadData();
    }

    // Force chart re-render on viewport resize
    let resizeTimer;
    const resizeKey = ref(0);
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { resizeKey.value++; }, 300);
    });

    // Method to handle interval change
    const handleIntervalChange = (newInterval) => {
      analyticEventStore.logEvent({
        action: `overview-change-interval`,
        metadata: {
          fromInterval: overviewStore.currentInterval,
          toInterval: newInterval
        }
      });
      overviewStore.currentInterval = newInterval;
      overviewStore.loadDataWithIntervals();
    };

    return {
      t,
      route,
      router,
      handleIntervalChange,
      overviewStore,
      organizationStore,
      analyticEventStore,
      resizeKey,
      showSettingsMenu,
      showSettingsModal
    };
  },
  computed: {
    currentInterval() {
      return this.overviewStore.currentInterval; // week, month, year
    },

    // Top x donors in time period by amount, currently not used
    topDonorsData() {
      let labels = ['Name', 'Amount'];

      if (this.overviewStore.topDonorsLoaded) {
        let rows = [];

        this.overviewStore.topDonors.forEach((donor) => {
          rows.push({
            'Name': `${donor.first_name} ${donor.last_name}`,
            'Amount': this.formattedMoney(donor.donations_sum)
          });
        });

        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },

    dateRangeMessage() {
      const intervalMessages = {
        daily: "Last 14 Days",
        weekly: "Last 4 Weeks",
        monthly: "Last Quarter",
        yearly: "Fiscal Year to Date",
      };
      return intervalMessages[this.currentInterval] || "";
    },

    // GIFT FREQUENCY
    // Donation types Pie chart, one time / recurring (subscriptions)
    donationTypeChartData() {
      // Chart Colors by label, add more if needed, gray as default
      const chartColours = {
        "One Time": CHART_COLORS.primary,
        "Recurring": CHART_COLORS.secondary
      };

      // Skeleton loader data
      if (!this.overviewStore.donationTypeDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: [CHART_COLORS.neutral.base],
              data: [1]
            }
          ]
        };
      }
      if (
        this.overviewStore.donationTypeDataLoaded
        && Object.keys(this.overviewStore.donationTypeData).length > 0) {

        let labels = Object.keys(this.overviewStore.donationTypeData).map((label) => {
          // Maps keys to proper labels "One Time", "Recurring"
          return label.split('_').map((word) => {
            //remove word donation from label ie -> "One Time", "Recurring"
            if (word === 'donation') return '';
            return word.charAt(0).toUpperCase() + word.slice(1);
          }).join(' ');
        });
        return {
          labels: labels,
          datasets: [
            {
              // Pareses chart colours, we were having an issue with mismatch between chart and left label colours
              backgroundColor: function (context) {
                // Function to ensure chart colors are always correct for the label
                const label = context.chart.data.labels[context.dataIndex]?.trim();
                if (label && chartColours[label]) {
                  return chartColours[label];
                } else {
                  return "gray";
                }
              },
              data: Object.values(this.overviewStore.donationTypeData)
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },

    // GIFT TYPE
    // Processor type pie chart Stripe, Helcim, Offline
    donationProcessorTypeChartData() {
      const chartColours = {
        "ONLINE": CHART_COLORS.primary,
        "OFFLINE": CHART_COLORS.secondary
      };
      // Skeleton loader data
      if (!this.overviewStore.processorTypeDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: [CHART_COLORS.neutral.base],
              data: [1]
            }
          ]
        };
      }
      if (this.overviewStore.processorTypeDataLoaded
        && Object.keys(this.overviewStore.processorTypeData).length > 0) {

        const labels = ["ONLINE", "OFFLINE"];
        let offline = this.overviewStore.processorTypeData['Processor::OfflineProcessor'] || 0;
        let stripe = this.overviewStore.processorTypeData['Processor::StripeProcessor'] || 0;
        let helcim = this.overviewStore.processorTypeData['Processor::HelcimProcessor'] || 0;

        const online = stripe + helcim;

        return {
          labels: labels,
          datasets: [
            {
              backgroundColor: function (context) {
                // Function to ensure chart colors are always correct for the label
                const label = context.chart.data.labels[context.dataIndex]?.trim();
                if (label && chartColours[label]) {
                  return chartColours[label];
                } else {
                  return "gray";
                }
              },
              data: [online, offline]
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },

    // TOP FUND
    // Donations per fund pie chart
    donationsByFundChartData() {
      if (!this.overviewStore.donationsByFundDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: [CHART_COLORS.neutral.base],
              data: [1]
            }
          ]
        };
      }
      if (this.overviewStore.donationsByFundDataLoaded
        && Object.keys(this.overviewStore.donationsByFundData).length > 0
        && Object.values(this.overviewStore.donationsByFundData).some((v) => v > 0)) {
        let labels = Object.keys(this.overviewStore.donationsByFundData).map((label) => {
          return label;
        });
        return {
          labels: labels,
          datasets: [
            {
              backgroundColor: [CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.chart.blue, CHART_COLORS.chart.purple, CHART_COLORS.chart.teal, CHART_COLORS.neutral.soft],
              data: Object.values(this.overviewStore.donationsByFundData)
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },

    /** Formatted percentage data for donation types
     * format -> {
     *   "Online": "70%",
     *   "Offline" "30%"
     * }
     * **/
    donationTypePercents() {
      if (!this.overviewStore.processorTypeDataLoaded) {
        return {
          "Online": null,
          "Offline": null
        };
      }
      let offline = this.overviewStore.processorTypeData['Processor::OfflineProcessor'] || 0;
      let stripe = this.overviewStore.processorTypeData['Processor::StripeProcessor'] || 0;
      let helcim = this.overviewStore.processorTypeData['Processor::HelcimProcessor'] || 0;

      const online = stripe + helcim;
      let total = offline + online;
      if (!total) return "";

      let onlinePercent = ((online / total) * 100).toFixed(0);
      let offlinePercent = 100 - onlinePercent;

      // Edge cases, don't show 0 if any records exist for either category if rounded to 0
      if (onlinePercent === '0' && online > 0) {
        onlinePercent = '<1 ';
      }
      if (offlinePercent === '0' && offline > 0) {
        offlinePercent = '<1 ';
      }

      return {
        "Online": `${onlinePercent}%`,
        "Offline": `${offlinePercent}%`
      };
    },

    /** Formatted percentage data for donation frequencies
     * format -> {
     *   "One Time": "70%",
     *   "Recurring" "30%"
     * }
     * **/
    donationFrequencyPercents() {
      if (!this.overviewStore.donationTypeDataLoaded) {
        return {
          "One Time": null,
          "Recurring": null
        };
      }
      let oneTime = this.overviewStore.donationTypeData['one_time_donation'] || 0;
      let recurring = this.overviewStore.donationTypeData['recurring_donation'] || 0;
      let total = oneTime + recurring;

      if (!total) return "";

      let oneTimePercent = ((oneTime / total) * 100).toFixed(0);
      let recurringPercent = 100 - oneTimePercent;

      // Edge cases, don't show 0 if any records exist for either category if rounded to 0
      if (oneTimePercent === '0' && oneTime > 0) {
        oneTimePercent = '<1 ';
      }
      if (recurringPercent === '0' && recurring > 0) {
        recurringPercent = '<1 ';
      }
      return {
        "One Time": `${oneTimePercent}%`,
        "Recurring": `${recurringPercent}%`
      };
    },

    /** Formatted percentage data for donations by fund
     * Returning only highest fund and 'other'
     * format -> {
     *   "Fund 1": "43%",
     *   "Other": "57%"
     * }
     * **/
    donationByFundPercentData() {
      try {
        if (!this.overviewStore.donationsByFundDataLoaded) return "";

        const donationData = this.overviewStore.donationsByFundData;
        if (!donationData || Object.keys(donationData).length === 0) return {};

        // Filter to only numeric values (safety against any metadata leaking through)
        const numericEntries = Object.entries(donationData).filter(([, v]) => typeof v === 'number');
        if (numericEntries.length === 0) return {};

        const total = numericEntries.reduce((sum, [, count]) => sum + count, 0);
        if (total === 0) return {};

        // Get the entry with the highest value (excluding 'other')
        let maxValue = 0;
        let maxKey = null;
        for (const [key, value] of numericEntries) {
          if (key.toLowerCase() === 'other') continue;
          if (!maxKey || value > maxValue) {
            maxKey = key;
            maxValue = value;
          }
        }

        if (!maxKey) return {};

        let highestPercent = Math.round((maxValue / total) * 100);
        let data = {};
        data[maxKey] = `${highestPercent}%`;
        data['Other'] = `${100 - highestPercent}%`;
        return data;
      } catch (e) {
        console.error('error parsing fund data', e);
        return {};
      }
    },

    lastUpdated() {
      const today = new Date();
      return today.toLocaleDateString('en-US', {
        month: 'short', // Short month format (e.g., "Oct")
        day: 'numeric', // Numeric day (e.g., "3")
        year: 'numeric'  // Numeric year (e.g., "2024")
      });
    },
    hasFunnelData() {
      return this.funnelSegments.length > 0;
    },
    funnelTotal() {
      return this.funnelSegments.reduce((sum, s) => sum + s.value, 0);
    },
    funnelAdditionalText() {
      if (!this.hasFunnelData) return this.dateRangeMessage;
      return `${this.dateRangeMessage} · ${this.formatNumber(this.funnelTotal)} total interactions`;
    },
    funnelSegments() {
      if (!this.overviewStore.donationFunnelDataLoaded) return [];
      const d = this.overviewStore.donationFunnelData;
      return [
        { label: 'Total Site Visits', value: d.visitors || 0, color: CHART_COLORS.chart.blue },
        { label: 'Donations Created', value: d.started || 0, color: CHART_COLORS.secondary },
        { label: 'Donations Completed', value: d.completed || 0, color: CHART_COLORS.primary },
        { label: 'Failed Donations', value: d.failed || 0, color: CHART_COLORS.chart.red }
      ].filter(s => s.value > 0);
    },

    funnelCompletionRate() {
      const d = this.overviewStore.donationFunnelData;
      if (!d?.started || d.started === 0) return "-";
      const rate = Math.min(Math.round((d.completed / d.started) * 100), 100);
      return rate === 0 ? "-" : rate + "%";
    },
    funnelStartRate() {
      const d = this.overviewStore.donationFunnelData;
      if (!d?.visitors || !d?.started) return "-";
      const pct = (d.started / d.visitors) * 100;
      if (pct > 100) return ">100%";
      const rate = Math.round(pct);
      return rate === 0 ? "<1%" : rate + "%";
    },
    funnelFailureRate() {
      const d = this.overviewStore.donationFunnelData;
      if (!d?.started) return "-";
      if (!d.failed || d.failed === 0) return "0%";
      const rate = Math.round((d.failed / d.started) * 100);
      return rate === 0 ? "<1%" : rate + "%";
    },

    donationFunnelChartData() {
      if (!this.overviewStore.donationFunnelDataLoaded) {
        return { labels: [''], datasets: [{ backgroundColor: [CHART_COLORS.neutral.base], data: [1] }] };
      }
      const d = this.overviewStore.donationFunnelData;
      const total = (d.completed || 0) + (d.abandoned || 0) + (d.failed || 0);
      if (!total) {
        return { labels: ['No data'], datasets: [] };
      }
      return {
        labels: ['Completed', 'Abandoned', 'Failed'],
        datasets: [{
          backgroundColor: [CHART_COLORS.chart.green, CHART_COLORS.chart.orange, CHART_COLORS.chart.red],
          data: [d.completed || 0, d.abandoned || 0, d.failed || 0]
        }]
      };
    },

    donationFunnelPercents() {
      if (!this.overviewStore.donationFunnelDataLoaded) return { "Completed": null, "Abandoned": null };
      const d = this.overviewStore.donationFunnelData;
      const total = (d.completed || 0) + (d.abandoned || 0) + (d.failed || 0);
      if (!total) return {};
      return {
        "Completed": d.completion_rate || "0%",
        "Abandoned": `${d.abandoned || 0}`
      };
    },

    donorBehaviorChartData() {
      if (!this.overviewStore.donorBehaviorDataLoaded) {
        return { labels: [''], datasets: [{ backgroundColor: [CHART_COLORS.neutral.base], data: [1] }] };
      }
      const d = this.overviewStore.donorBehaviorData;
      if (!d.total_donors) {
        return { labels: ['No data'], datasets: [] };
      }
      return {
        labels: ['New Donors', 'Returning Donors'],
        datasets: [{
          backgroundColor: [CHART_COLORS.secondary, CHART_COLORS.primary],
          data: [d.new_donors || 0, d.returning_donors || 0]
        }]
      };
    },

    donorBehaviorPercents() {
      if (!this.overviewStore.donorBehaviorDataLoaded) return { "New": null, "Returning": null };
      const d = this.overviewStore.donorBehaviorData;
      const total = d.total_donors || 0;
      if (!total) return {};
      if (!d.returning_donors) return { "Donors": `${total}`, "Returning": "—" };
      const newPct = Math.round(((d.new_donors || 0) / total) * 100);
      return { "New": `${newPct}%`, "Returning": `${100 - newPct}%` };
    },

    qrAnalyticsChartData() {
      if (!this.overviewStore.qrAnalyticsDataLoaded) {
        return { labels: [''], datasets: [{ backgroundColor: [CHART_COLORS.neutral.base], data: [1] }] };
      }
      const d = this.overviewStore.qrAnalyticsData;
      if (!d.landing_page && !d.donatable_page && !d.giving_form) {
        return { labels: ['No QR scans'], datasets: [] };
      }
      return {
        labels: ['Landing Page', 'Fund/Campaign', 'Giving Form'],
        datasets: [{
          backgroundColor: [CHART_COLORS.primary, CHART_COLORS.secondary, CHART_COLORS.chart.blue],
          data: [d.landing_page || 0, d.donatable_page || 0, d.giving_form || 0]
        }]
      };
    },

    kpiItems() {
      if (!this.overviewStore.kpiSummaryDataLoaded) return [];
      const d = this.overviewStore.kpiSummaryData;
      return [
        { label: 'Avg Gift Size', value: this.formattedMoney(d.avg_gift), trend: d.trends?.avg_gift },
        { label: 'Active Donors', value: this.formatNumber(d.active_donors), trend: d.trends?.active_donors },
        { label: 'Donor Retention', value: `${d.retention_rate}%`, sublabel: 'vs previous period' },
        { label: 'Completion Rate', value: `${d.completion_rate}%`, sublabel: 'of started donations' }
      ];
    },

    qrAnalyticsPercents() {
      if (!this.overviewStore.qrAnalyticsDataLoaded) return { "Total Scans": null };
      const d = this.overviewStore.qrAnalyticsData;
      const total = d.total_scans || 0;
      if (!total) return {};
      const landingPct = Math.round(((d.landing_page || 0) / total) * 100);
      const donatablePct = Math.round(((d.donatable_page || 0) / total) * 100);
      const givingFormPct = 100 - landingPct - donatablePct;
      return { "Landing Page": `${landingPct}%`, "Fund/Campaign": `${donatablePct}%`, "Giving Form": `${givingFormPct}%`};
    },

    websiteTrafficLineData() {
      if (!this.overviewStore.websiteTrafficDataLoaded) return null;
      const data = this.overviewStore.websiteTrafficData;
      const landingData = data?.landing_chart_data;
      const donationData = data?.donation_chart_data;
      if ((!landingData || Object.keys(landingData).length === 0) &&
          (!donationData || Object.keys(donationData).length === 0)) return null;

      const allDates = [...new Set([
        ...Object.keys(landingData || {}),
        ...Object.keys(donationData || {})
      ])].sort();

      const labels = allDates.map(date => {
        const d = new Date(date + 'T00:00:00');
        return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
      });

      return {
        labels,
        datasets: [
          {
            label: 'Landing Page',
            data: allDates.map(d => landingData?.[d] || 0),
            borderColor: CHART_COLORS.primary,
            backgroundColor: 'rgba(16, 190, 90, 0.1)',
            fill: true,
            tension: 0.3
          },
          {
            label: 'Giving Form',
            data: allDates.map(d => donationData?.[d] || 0),
            borderColor: CHART_COLORS.accent,
            backgroundColor: 'rgba(6, 182, 212, 0.1)',
            fill: true,
            tension: 0.3
          }
        ]
      };
    },

    // Use this to hide the interval toggle if no charts get displayed
    canViewAnyCharts() {
      return this.hasOrganizationPermissions('donation_report') ||
        this.hasOrganizationPermissions('donations_per_fund') ||
        this.hasOrganizationPermissions('donation_processor_types') ||
        this.hasOrganizationPermissions('donation_types');
    },

    orderedWidgets() {
      const config = this.organizationStore.organization?.effective_dashboard_config;
      if (!config?.widgets) return [];
      return [...config.widgets]
        .filter(w => w.visible && w.key !== 'gifts_projection')
        .sort((a, b) => a.position - b.position);
    }
  },
  watch: {
    'overviewStore.organizationId'() {
      this.overviewStore.loadData();
    }
  },
  methods: {
    formattedMoney(val) {
      if(!val) return "-";
      return ApplicationHelper.formatMoney(val, true);
    },

    formatNumber(val) {
      if(!val) return "-";
      return ApplicationHelper.formatNumber(val);
    },

    formatCacheDate(isoString) {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    },

    translateTraceableType(panel) {
      let traceable_type = "";
      if (panel === "campaigns") {
        traceable_type = "Campaign";
      }
      if (panel === "funds") {
        traceable_type = "Fund";
      }
      return traceable_type;
    },

    goToRecord(id, panel) {
      const traceable_type = this.translateTraceableType(panel);
      this.analyticEventStore.logEvent({
        action: `overview-view-${traceable_type.toLowerCase()}`,
        traceable_type: traceable_type,
        traceable_id: id
      });
      this.router.push({
        name: "client-dashboard",
        params: {
          ...this.route.params,
          panel: panel,
          id: id
        }
      });
    },

    hasOrganizationPermissions(permissionKey) {
      // If the key doesn't exist then the permissions aren't loaded, return true as a default so we don't break the skeleton loaders
      if (!this.organizationStore.permissions || !this.organizationStore.permissions.hasOwnProperty(permissionKey)) return true;
      return this.organizationStore.permissions[permissionKey];
    },

    trendIcon(direction) {
      if (direction === 'up') return 'fa-solid fa-arrow-up text-green-500';
      if (direction === 'down') return 'fa-solid fa-arrow-down text-red-500';
      return 'fa-solid fa-minus text-gray-400';
    },

    timeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },

    goToDonation(donationSlug) {
      this.router.push({
        name: "client-dashboard",
        params: { ...this.route.params, panel: "gifts", id: donationSlug }
      });
    },
    printDashboard() {
      this.showSettingsMenu = false;
      window.print();
    },
    async handleSettingsSave(newConfig) {
      await this.organizationStore.organizationService.update(
        this.organizationStore.organizationId,
        { dashboard_config: newConfig }
      );
      this.organizationStore.organization.effective_dashboard_config = newConfig;
      this.analyticEventStore.logEvent({ action: 'overview-edit-layout-saved' });
      this.showSettingsModal = false;
    },

    openEditWidgets() {
      this.showSettingsModal = true;
      this.showSettingsMenu = false;
      this.analyticEventStore.logEvent({ action: 'overview-edit-layout-open' });
    },


  }
};
</script>

<template>
  <div class="w-full p-4 pt-2">
    <!-- Print-only header -->
    <div class="print-header">
      <h1>{{ organizationStore.organization?.name }} - Dashboard Overview</h1>
      <p>Generated {{ new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }} · {{ currentInterval.charAt(0).toUpperCase() + currentInterval.slice(1) }} view</p>
    </div>

    <div class="flex justify-between items-center mb-4 no-print">
      <div class="flex items-center gap-2">
        <p class="font-bold text-2xl">
          Overview
        </p>
        <!-- Settings: Edit Widget & Print -->
        <div
          v-if="organizationStore.pagePermissions.dashboard_settings"
          class="relative flex items-center"
        >
          <button
            class="text-gray-400 hover:text-gray-600"
            @click="showSettingsMenu = !showSettingsMenu"
          >
            <i class="fa-solid fa-gear mt-2 text-lg" />
          </button>
          <div
            v-if="showSettingsMenu"
            class="fixed inset-0 z-10"
            @click="showSettingsMenu = false"
          />
          <div
            v-if="showSettingsMenu"
            class="settings-menu"
          >
            <button
              class="settings-menu-item"
              @click="openEditWidgets"
            >
              <i class="fa-solid fa-pen-to-square mr-2" />Edit Widgets
            </button>
            <!--            <button class="settings-menu-item" @click="printDashboard">-->
            <!--              <i class="fa-solid fa-print mr-2" />Print-->
            <!--            </button>-->
          </div>
        </div>
      </div>

      <IntervalToggle
        v-if="canViewAnyCharts"
        @interval-change="handleIntervalChange"
      />
    </div>

    <!--    Message for editors  -->
    <div
      v-if="!canViewAnyCharts"
      class="flex flex-col items-center justify-center h-64 text-gray-500"
    >
      <i class="fa-solid fa-lock text-3xl mb-3 text-gray-300" />
      <p class="text-lg font-medium">
        You don't have permission to view the dashboard overview.
      </p>
      <p class="text-sm">
        Contact your organization admin for access.
      </p>
    </div>

    <div
      v-if="canViewAnyCharts"
      :key="organizationStore.organizationId + '-' + resizeKey"
      class="overview-grid"
    >
      <!-- Hero: Donation Projections — 3 wide, 2 tall -->
      <DataCard
        v-if="hasOrganizationPermissions('donation_report')"
        id="overview-donation-projections-data-card"
        :key="overviewStore.periodDonationCount?.toString()"
        class="grid-cell grid-3x2"
        title="Gifts"
        :additional-text="overviewStore.donationDataDateRange"
        left-sidebar
        title-icon="hand-heart"
        icon-color="#10be5a"
        :left-sidebar-data="[
          { label: 'Total Gifts', value: formatNumber(overviewStore.periodDonationCount)},
          { label: 'Total Dollars', value: formattedMoney(overviewStore.periodDonationSum) }
        ]"
        loaded
      >
        <template #leftSidebarExtras>
          <tooltip
            message="Shows your projected vs. actual gifts over the selected interval so you can track whether you're on pace."
            variant="question"
            size="12px"
            class="ml-1 self-center"
            :position="{ bottom: '100%', right: '0', transform: 'translateX(-40%)', maxWidth: '300px' }"
          />
        </template>
        <donation-payment-chart class="min-h-[300px]">
          <template #donation-chart>
            <div
              v-if="!overviewStore.donationDataLoaded"
              class="chart-skeleton-line"
            >
              <svg
                viewBox="0 0 400 200"
                preserveAspectRatio="none"
                class="skeleton-svg"
              >
                <defs>
                  <linearGradient
                    id="shimmerGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stop-color="#e5e7eb"
                    /><stop
                      offset="50%"
                      stop-color="#f3f4f6"
                    /><stop
                      offset="100%"
                      stop-color="#e5e7eb"
                    />
                    <animate
                      attributeName="x1"
                      from="-100%"
                      to="100%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="x2"
                      from="0%"
                      to="200%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M0,140 C30,130 60,120 90,110 C120,100 150,80 180,90 C210,100 240,70 270,60 C300,50 330,65 360,55 L400,45"
                  fill="none"
                  stroke="url(#shimmerGrad)"
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <path
                  d="M0,140 C30,130 60,120 90,110 C120,100 150,80 180,90 C210,100 240,70 270,60 C300,50 330,65 360,55 L400,45 L400,200 L0,200 Z"
                  fill="url(#shimmerGrad)"
                  opacity="0.15"
                />
              </svg>
            </div>
            <!--    Not Enough Data Message -> Used for Weekly, Monthly and Yearly Projections-->
            <div
              v-else-if="!overviewStore.hasYearlyProjectionData"
              class="flex items-center justify-center h-[280px] text-gray-500 text-sm"
            >
              <div class="text-center">
                <i class="fa-solid fa-chart-line text-gray-300 text-2xl mb-2" />
                <p>Not enough data to report on.</p>
              </div>
            </div>
            <transition
              v-else
              name="fade"
            >
              <BaseLineChart
                v-if="overviewStore.donationDataLoaded"
                :data="overviewStore.donationProjectionChartData"
                :fiscal-year-start-label="overviewStore.donationData?.fiscal_year_start_label || ''"
              />
            </transition>
          </template>
          <template #payment-chart>
            <div
              v-if="!overviewStore.paymentDataLoaded"
              class="chart-skeleton-line"
            >
              <svg
                viewBox="0 0 400 200"
                preserveAspectRatio="none"
                class="skeleton-svg"
              >
                <defs>
                  <linearGradient
                    id="shimmerGradPayment"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stop-color="#e5e7eb"
                    /><stop
                      offset="50%"
                      stop-color="#f3f4f6"
                    /><stop
                      offset="100%"
                      stop-color="#e5e7eb"
                    />
                    <animate
                      attributeName="x1"
                      from="-100%"
                      to="100%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="x2"
                      from="0%"
                      to="200%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M0,140 C30,130 60,120 90,110 C120,100 150,80 180,90 C210,100 240,70 270,60 C300,50 330,65 360,55 L400,45"
                  fill="none"
                  stroke="url(#shimmerGradPayment)"
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <path
                  d="M0,140 C30,130 60,120 90,110 C120,100 150,80 180,90 C210,100 240,70 270,60 C300,50 330,65 360,55 L400,45 L400,200 L0,200 Z"
                  fill="url(#shimmerGradPayment)"
                  opacity="0.15"
                />
              </svg>
            </div>
            <div
              v-else-if="!overviewStore.hasYearlyPaymentProjectionData"
              class="flex items-center justify-center h-[280px] text-gray-500 text-sm"
            >
              <div class="text-center">
                <i class="fa-solid fa-chart-line text-gray-300 text-2xl mb-2" />
                <p>Not enough data to report on.</p>
              </div>
            </div>
            <BaseLineChart
              v-else
              :data="overviewStore.paymentChartData"
              y-prefix="$"
              :fiscal-year-start-label="overviewStore.paymentData?.fiscal_year_start_label || ''"
            />
          </template>
        </donation-payment-chart>
      </DataCard>

      <div
        v-if="orderedWidgets.length"
        class="overview-grid-draggable"
      >
        <div
          v-for="widget in orderedWidgets"
          :key="widget.key"
          :class="['grid-cell', `grid-${widget.size}`, 'pop-in']"
        >
          <!-- Campaign Calendar -->
          <DataCard
            v-if="widget.key === 'campaign_calendar'"
            title="Campaign Calendar"
            title-icon="hand-heart"
            icon-color="#10be5a"
            :loaded="overviewStore.calendarDataLoaded"
          >
            <template #titleExtras>
              <tooltip
                message="View your active and upcoming campaigns on a monthly calendar. Colored bars show campaign duration. Up to 6 campaigns are shown at a time."
                variant="question"
                size="12px"
                class="ml-1"
                :position="{ bottom: '100%', left: '50%', transform: 'translateX(-80%)', maxWidth: '280px' }"
              />
            </template>
            <CampaignCalendar
              :campaigns="overviewStore.calendarData.campaigns || []"
              :holidays="overviewStore.calendarData.holidays || []"
              :scope-start="overviewStore.calendarData.scope_start"
              :scope-end="overviewStore.calendarData.scope_end"
              :year="overviewStore.calendarYear"
              :month="overviewStore.calendarMonth"
              :loaded="overviewStore.calendarDataLoaded"
              @navigate="overviewStore.navigateCalendar($event)"
              @today="overviewStore.resetCalendarToToday()"
              @select-campaign="goToRecord($event, 'campaigns')"
            />
          </DataCard>

          <!-- Recent Gifts -->
          <DataCard
            v-else-if="widget.key === 'recent_gifts'"
            class="recent-gifts-card"
            title="Recent Gifts"
            title-icon="hand-heart"
            icon-color="#10be5a"
            :loaded="overviewStore.recentDonationsDataLoaded"
          >
            <template #titleExtras>
              <tooltip
                message="Your most recent donations listed by amount and fund. Click any row to view the full donation details. Donor names are hidden for privacy."
                variant="question"
                size="12px"
                class="ml-1"
                :position="{ bottom: '-300%', right: '50%', transform: 'translateX(-65%)', maxWidth: '250px' }"
              />
            </template>
            <RecentDonationsCard
              :donations="overviewStore.recentDonationsData"
              :loaded="overviewStore.recentDonationsDataLoaded"
              @select="goToDonation"
            />
          </DataCard>

          <!-- Website Traffic -->
          <DataCard
            v-else-if="widget.key === 'website_traffic'"
            class="website-traffic-card"
            title="Website Traffic"
            :additional-text="dateRangeMessage"
            left-sidebar
            compact="tablet"
            title-icon="chart-line"
            icon-color="#10be5a"
            :left-sidebar-data="[
              { label: 'Total Events', value: formatNumber(overviewStore.websiteTrafficData?.total) },
              { label: 'Landing Page', value: formatNumber(overviewStore.websiteTrafficData?.contexts?.landing) },
              { label: 'Giving Form', value: formatNumber(overviewStore.websiteTrafficData?.contexts?.donation) }
            ]"
            :loaded="overviewStore.websiteTrafficDataLoaded"
          >
            <template #leftSidebarExtras>
              <tooltip
                message="Tracks page views and interactions across your public website, donation pages, and admin portal for the selected time period."
                variant="question"
                size="12px"
                :position="{ bottom: '100%', left: '50%', transform: 'translateX(-70%)', maxWidth: '300px' }"
              />
            </template>
            <div
              v-if="!overviewStore.websiteTrafficDataLoaded"
              class="chart-skeleton-line"
            >
              <svg
                viewBox="0 0 400 200"
                preserveAspectRatio="none"
                class="skeleton-svg"
              >
                <defs>
                  <linearGradient
                    id="shimmerGradTraffic"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stop-color="#e5e7eb"
                    /><stop
                      offset="50%"
                      stop-color="#f3f4f6"
                    /><stop
                      offset="100%"
                      stop-color="#e5e7eb"
                    />
                    <animate
                      attributeName="x1"
                      from="-100%"
                      to="100%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="x2"
                      from="0%"
                      to="200%"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                  </linearGradient>
                </defs>
                <path
                  d="M0,140 C30,130 60,120 90,110 C120,100 150,80 180,90 C210,100 240,70 270,60 C300,50 330,65 360,55 L400,45"
                  fill="none"
                  stroke="url(#shimmerGradTraffic)"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <BaseLineChart
              v-else-if="websiteTrafficLineData"
              :data="websiteTrafficLineData"
              legend-align="end"
            />
            <div
              v-else
              class="flex items-center justify-center h-[280px] text-gray-500 text-sm"
            >
              <div class="text-center">
                <i class="fa-solid fa-chart-line text-gray-300 text-2xl mb-2" /><p>No website traffic data available for this period.</p>
              </div>
            </div>
          </DataCard>

          <!-- Key Metrics -->
          <DataCard
            v-else-if="widget.key === 'key_metrics'"
            title="Key Metrics"
            title-icon="chart-line"
            icon-color="#10be5a"
            :additional-text="dateRangeMessage"
            :loaded="overviewStore.kpiSummaryDataLoaded"
          >
            <template #titleExtras>
              <tooltip
                message="A snapshot of your key performance indicators. Arrows show whether each metric improved or declined compared to the previous period."
                variant="question"
                size="12px"
                class="ml-1"
                :position="{ bottom: '100%', left: '50%', transform: 'translateX(-60%)', maxWidth: '300px' }"
              />
            </template>
            <KpiCard
              :items="kpiItems"
              :loaded="overviewStore.kpiSummaryDataLoaded"
            />
          </DataCard>

          <!-- Gift Frequency -->
          <doughnut-chart-data-card
            v-else-if="widget.key === 'gift_frequency'"
            title="Gift Frequency"
            :subtitle="dateRangeMessage"
            :chart-data="donationTypeChartData"
            :percent-data="donationFrequencyPercents"
            :cache-info="overviewStore.donationTypeDataCache"
            :tooltip-message="'One‐time vs. recurring donations.'"
          />

          <!-- Gift Type -->
          <doughnut-chart-data-card
            v-else-if="widget.key === 'gift_type'"
            title="Gift Type"
            :subtitle="dateRangeMessage"
            :chart-data="donationProcessorTypeChartData"
            :percent-data="donationTypePercents"
            :cache-info="overviewStore.processorTypeDataCache"
            :tooltip-message="'Online processors vs. offline gifts.'"
          />

          <!-- Top Funds -->
          <doughnut-chart-data-card
            v-else-if="widget.key === 'top_funds'"
            title="Top Funds"
            :subtitle="dateRangeMessage"
            :chart-data="donationsByFundChartData"
            :percent-data="donationByFundPercentData"
            format-as-money
            :cache-info="overviewStore.donationsByFundDataCache"
            :tooltip-message="'Which funds your donors gave to most.'"
          />

          <!-- Donation Funnel -->
          <DataCard
            v-else-if="widget.key === 'donation_funnel'"
            title="Donation Funnel"
            :additional-text="funnelAdditionalText"
            title-icon="chart-line"
            icon-color="#10be5a"
            :loaded="overviewStore.donationFunnelDataLoaded"
          >
            <template #titleExtras>
              <tooltip
                message="Shows the full donation journey: how many people visited your page, started a donation, completed it, or had a failure."
                variant="question"
                size="12px"
                :position="{ bottom: '100%', left: '50%', transform: 'translateX(-70%)', maxWidth: '300px' }"
              />
            </template>
            <FunnelBar
              :segments="funnelSegments"
              :loaded="overviewStore.donationFunnelDataLoaded"
              :completion-rate="funnelCompletionRate"
            />
          </DataCard>

          <!-- Donor Retention -->
          <doughnut-chart-data-card
            v-else-if="widget.key === 'donor_retention'"
            title="Donor Retention"
            :subtitle="dateRangeMessage"
            :chart-data="donorBehaviorChartData"
            :percent-data="donorBehaviorPercents"
            :cache-info="overviewStore.donorBehaviorDataCache"
            :tooltip-message="'New first-time donors vs. returning donors.'"
          />

          <!-- QR Code Scans -->
          <doughnut-chart-data-card
            v-else-if="widget.key === 'qr_scans'"
            title="QR Code Scans"
            :subtitle="dateRangeMessage"
            :chart-data="qrAnalyticsChartData"
            :percent-data="qrAnalyticsPercents"
            :cache-info="overviewStore.qrAnalyticsDataCache"
            :tooltip-message="'QR code scans from your pages.'"
          />

          <!-- Community Heat Map -->
          <community-heat-map
            v-else-if="widget.key === 'community_map'"
          />
        </div>
      </div>
    </div>
    <DashboardSettingsModal
      v-if="showSettingsModal"
      :config="organizationStore.organization.effective_dashboard_config"
      @save="handleSettingsSave"
      @close="showSettingsModal = false"
    />
  </div>
</template>

<style lang="scss" scoped>
.print-header {
  display: none;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  margin: 24px 0;
  /* overflow-x: hidden; — removed to allow tooltips to show above cards */
}

.grid-cell {
  grid-column: span 1;
  grid-row: span 1;
  min-height: 0;
}

.overview-grid-draggable > .grid-cell {
  display: flex;
  flex-direction: column;

  > * { flex: 1; min-height: 0; }
  :deep(.base-container) { height: 100%; }
}

.grid-1x1 {
  grid-column: span 1;
  grid-row: span 1;
}

.grid-3x2 {
  grid-column: span 3;
  grid-row: span 2;
}

.grid-2x1 {
  grid-column: span 2;
}

.grid-1x2 {
  grid-row: span 2;
}

.recent-gifts-card {
  max-height: 580px;
  :deep(.base-container) { height: 100%; display: flex; flex-direction: column; overflow: hidden; }
  :deep(.data-card-container) { flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
  :deep(.main-content) { flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
}

.grid-2x2 {
  grid-column: span 2;
  grid-row: span 2;
  max-height: 580px;
}

/* Line chart skeleton loader */
.chart-skeleton-line {
  max-height: 280px;
  min-height: 180px;
  padding: 20px 10px;
  display: flex;
  align-items: center;
}

.skeleton-svg {
  width: 100%;
  height: 100%;
}

/* Fade transition for charts */
.fade-enter-active { transition: opacity 0.4s ease; }
.fade-enter-from { opacity: 0; }

/* Pop-in animation for cards */
.pop-in {
  animation: popIn 0.35s ease-out both;
}

.overview-grid-draggable {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: 278px;
  grid-auto-flow: dense;
  gap: 24px;
  grid-column: 1 / -1;
}

.settings-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 20;
  overflow: hidden;
}

.settings-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  font-size: 0.875rem;
  color: #374151;
  text-align: left;
  transition: background 0.1s;

  &:hover { background: #f3f4f6; }
}
.pop-in:nth-child(1) { animation-delay: 0s; }
.pop-in:nth-child(2) { animation-delay: 0.08s; }
.pop-in:nth-child(3) { animation-delay: 0.16s; }
.pop-in:nth-child(4) { animation-delay: 0.24s; }
.pop-in:nth-child(5) { animation-delay: 0.32s; }
.pop-in:nth-child(6) { animation-delay: 0.40s; }

@keyframes popIn {
  0% { opacity: 0; transform: scale(0.95) translateY(8px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

/* Responsive: xl — drop to 2 columns (covers laptops ≤1200px and iPad landscape) */
@media screen and (max-width: 1200px) {
  .overview-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin: 16px 0; }
  .overview-grid-draggable { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  .grid-3x2 { grid-column: span 2; } /* keep 2-row height for chart breathing room */
  .grid-2x1 { grid-column: span 2; }
  .grid-2x2 { grid-column: span 2; } /* keep 2-row height */
  .grid-1x2 { grid-row: span 1; max-height: none; }
}

/* Responsive: md — single column */
@media screen and (max-width: 768px) {
  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    margin: 12px 0;
  }
  .overview-grid-draggable {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    grid-auto-rows: auto;
  }
  .grid-3x2, .grid-2x1, .grid-1x2, .grid-2x2 {
    grid-column: span 1;
    grid-row: span 1;
  }

  .website-traffic-card :deep(.main-content) {
    min-height: 200px;
  }
}
</style>
